/** Tokens visuais — Nova evolução clínica (frontend-design + klinvia-tokens) */
import { AGENDA_THEME } from '../../pages/agendaTheme';
import { clinicLargeFormDialogPaperSx } from '../../utils/mobileFormLayout';

export const EVOLUTION_THEME = {
  ...AGENDA_THEME,
  sectionHeaderBg: AGENDA_THEME.tealSoft,
  sectionTitle: AGENDA_THEME.tealDark,
  aiGradient: `linear-gradient(135deg, ${AGENDA_THEME.teal} 0%, ${AGENDA_THEME.tealDark} 100%)`,
  aiGradientHover: `linear-gradient(135deg, ${AGENDA_THEME.tealBright} 0%, ${AGENDA_THEME.teal} 100%)`,
  signedBg: '#ECFDF5',
  signedBorder: '#BBF7D0',
  signedText: '#166534',
  draftBg: '#FFFBEB',
  draftBorder: '#FCD34D',
  draftText: '#92400E',
  vitalsAccent: AGENDA_THEME.teal,
  vitalsAccentAlt: '#0891B2',
} as const;

export const evolutionClinicalGridBg = {
  backgroundColor: EVOLUTION_THEME.muted,
  backgroundImage: `
    linear-gradient(${EVOLUTION_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${EVOLUTION_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const evolutionDialogPaperSx = (
  isMobile: boolean,
  embedded: boolean,
  compact = false,
) => {
  if (embedded) {
    return {
      m: 0,
      width: '100%',
      height: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden',
      border: `1px solid ${EVOLUTION_THEME.borderSoft}`,
    };
  }
  if (isMobile) return undefined;
  return {
    ...clinicLargeFormDialogPaperSx(false),
    border: `1px solid ${EVOLUTION_THEME.borderSoft}`,
    boxShadow: EVOLUTION_THEME.dialogShadow,
  };
};

export const evolutionDialogHeaderSx = {
  ...evolutionClinicalGridBg,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  pb: 1,
  flexShrink: 0,
  px: { xs: 2, sm: 3 },
  pt: { xs: 1.5, sm: 2 },
  borderBottom: `1px solid ${EVOLUTION_THEME.borderSoft}`,
};

export const evolutionDialogTitleSx = {
  fontWeight: 700,
  color: EVOLUTION_THEME.navy,
  letterSpacing: '-0.02em',
};

export const evolutionDialogSubtitleSx = {
  color: EVOLUTION_THEME.slate,
  display: 'block',
};

export const evolutionDraftHintSx = {
  color: EVOLUTION_THEME.slateFaint,
  display: 'block',
  mt: 0.35,
};

export const evolutionDraftStatusSx = (isError: boolean) => ({
  display: 'block',
  mt: 0.35,
  color: isError ? EVOLUTION_THEME.cancelledText : EVOLUTION_THEME.teal,
  fontWeight: 600,
});

export const evolutionAiButtonSx = {
  background: EVOLUTION_THEME.aiGradient,
  color: '#fff',
  borderRadius: 2,
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: 12,
  px: 1.5,
  '&:hover': { background: EVOLUTION_THEME.aiGradientHover },
};

export const evolutionDialogContentSx = {
  pt: 2,
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  px: { xs: 2, sm: 3 },
  pb: 1,
  bgcolor: EVOLUTION_THEME.paper,
};

export const evolutionAccordionSx = {
  mb: 2,
  borderRadius: 2,
  '&:before': { display: 'none' },
  boxShadow: EVOLUTION_THEME.shadow,
  border: `1px solid ${EVOLUTION_THEME.borderSoft}`,
  bgcolor: EVOLUTION_THEME.paper,
  overflow: 'hidden',
};

/** Faixa compacta: profissional + diagnóstico/CID (sem accordion). */
export const evolutionIdentificationPaperSx = {
  mb: 1.5,
  px: { xs: 1.25, sm: 1.5 },
  py: { xs: 0.85, sm: 1 },
  borderRadius: 1.5,
  border: `1px solid ${EVOLUTION_THEME.borderSoft}`,
  bgcolor: EVOLUTION_THEME.muted,
  boxShadow: 'none',
};

export const evolutionIdentificationPaperSxFor = (compact: boolean) =>
  compact
    ? { ...evolutionIdentificationPaperSx, mb: 1, py: 0.65, px: { xs: 1, sm: 1.25 } }
    : evolutionIdentificationPaperSx;

export const evolutionSectionPaperSx = {
  mb: 2,
  border: `1px solid ${EVOLUTION_THEME.borderSoft}`,
  borderRadius: 2,
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

export const evolutionSectionHeaderBarSx = {
  px: 2,
  py: 1.25,
  bgcolor: EVOLUTION_THEME.sectionHeaderBg,
  borderBottom: `1px solid ${EVOLUTION_THEME.borderSoft}`,
};

export const evolutionSectionTitleSx = {
  fontWeight: 800,
  color: EVOLUTION_THEME.sectionTitle,
  letterSpacing: '0.12em',
};

export const evolutionSectionHeadingSx = {
  fontWeight: 800,
  fontSize: '0.8rem',
  letterSpacing: '0.06em',
  color: EVOLUTION_THEME.navy,
};

export const evolutionClickableRowSx = (disabled: boolean) => ({
  px: 2,
  py: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  cursor: disabled ? 'default' : 'pointer',
  bgcolor: disabled ? EVOLUTION_THEME.mutedAlt : EVOLUTION_THEME.muted,
  borderBottom: `1px solid ${EVOLUTION_THEME.borderSoft}`,
  opacity: disabled ? 0.7 : 1,
  transition: EVOLUTION_THEME.transition,
  '&:hover': {
    bgcolor: disabled ? EVOLUTION_THEME.mutedAlt : EVOLUTION_THEME.tealSoft,
  },
});

export const evolutionClickableRowSxFor = (disabled: boolean, compact: boolean) =>
  compact
    ? { ...evolutionClickableRowSx(disabled), px: 1.25, py: 0.85, gap: 1 }
    : evolutionClickableRowSx(disabled);

export const evolutionListDropzoneSx = (compact: boolean) => ({
  mt: compact ? 0.5 : 1.25,
  minHeight: compact ? 0 : 44,
  maxHeight: compact ? 120 : 168,
  overflow: 'auto',
  display: 'flex',
  flexWrap: 'wrap',
  gap: compact ? 0.5 : 1,
  alignContent: 'flex-start',
});

export const evolutionCheckboxSx = {
  color: EVOLUTION_THEME.teal,
  '&.Mui-checked': { color: EVOLUTION_THEME.tealDark },
};

export const evolutionPresetChipSx = (selected: boolean) =>
  selected
    ? { bgcolor: EVOLUTION_THEME.teal, color: '#fff' }
    : { borderColor: EVOLUTION_THEME.border, color: EVOLUTION_THEME.tealDark };

export const evolutionLinkButtonSx = {
  mt: 1,
  textTransform: 'none' as const,
  color: EVOLUTION_THEME.teal,
};

export const evolutionDialogFooterSx = {
  px: { xs: 2, sm: 3 },
  py: 2,
  flexShrink: 0,
  flexWrap: 'wrap' as const,
  gap: 2,
  justifyContent: 'space-between',
  alignItems: 'center',
  borderTop: `1px solid ${EVOLUTION_THEME.borderSoft}`,
  bgcolor: EVOLUTION_THEME.muted,
};

export const evolutionDialogContentSxFor = (compact: boolean) =>
  compact
    ? {
        ...evolutionDialogContentSx,
        pt: 1,
        px: { xs: 1.25, sm: 1.75 },
        pb: 0.5,
      }
    : evolutionDialogContentSx;

export const evolutionDialogFooterSxFor = (compact: boolean) =>
  compact
    ? {
        ...evolutionDialogFooterSx,
        px: { xs: 1.25, sm: 1.75 },
        py: 0.75,
        gap: 0.75,
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        flexWrap: 'wrap' as const,
      }
    : evolutionDialogFooterSx;

export const evolutionSectionPaperSxFor = (compact: boolean) =>
  compact
    ? { ...evolutionSectionPaperSx, mb: 1 }
    : evolutionSectionPaperSx;

export const evolutionSectionHeaderBarSxFor = (compact: boolean) =>
  compact
    ? { ...evolutionSectionHeaderBarSx, px: 1.25, py: 0.75 }
    : evolutionSectionHeaderBarSx;

export const evolutionSectionBodySx = (compact: boolean) => ({
  p: compact ? 1 : 2,
});

export const evolutionStatusBannerSx = (signed: boolean, compact = false) =>
  compact
    ? {
        px: 0,
        py: 0,
        borderRadius: 0,
        bgcolor: 'transparent',
        border: 'none',
        maxWidth: '100%',
        flex: 1,
        minWidth: 0,
      }
    : {
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: signed ? EVOLUTION_THEME.signedBg : EVOLUTION_THEME.draftBg,
        border: `1px solid ${signed ? EVOLUTION_THEME.signedBorder : EVOLUTION_THEME.draftBorder}`,
        maxWidth: { xs: '100%', sm: 420 },
      };

export const evolutionStatusBannerTextSx = (signed: boolean) => ({
  color: signed ? EVOLUTION_THEME.signedText : EVOLUTION_THEME.draftText,
  fontWeight: 600,
});

export const evolutionStatusBannerTextSxFor = (signed: boolean, compact: boolean) =>
  compact
    ? {
        ...evolutionStatusBannerTextSx(signed),
        fontWeight: 500,
        fontSize: '0.72rem',
        lineHeight: 1.35,
        color: signed ? EVOLUTION_THEME.signedText : EVOLUTION_THEME.slateMid,
      }
    : evolutionStatusBannerTextSx(signed);

export const evolutionCancelButtonSx = {
  color: EVOLUTION_THEME.slate,
  textTransform: 'none' as const,
};

export const evolutionOutlinedButtonSx = {
  textTransform: 'none' as const,
  borderColor: EVOLUTION_THEME.teal,
  color: EVOLUTION_THEME.tealDark,
  '&:hover': {
    borderColor: EVOLUTION_THEME.tealDark,
    bgcolor: EVOLUTION_THEME.tealSoft,
  },
};

export const evolutionSecondaryOutlinedButtonSx = {
  textTransform: 'none' as const,
  borderColor: EVOLUTION_THEME.borderSoft,
  color: EVOLUTION_THEME.slateMid,
};

export const evolutionPrimaryButtonSx = {
  minWidth: 140,
  fontWeight: 800,
  letterSpacing: 0.06,
  textTransform: 'none' as const,
  bgcolor: EVOLUTION_THEME.teal,
  '&:hover': { bgcolor: EVOLUTION_THEME.tealDark },
};

export const evolutionAiDialogHeaderSx = {
  background: EVOLUTION_THEME.aiGradient,
  px: 3,
  py: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const evolutionLoadingSkeletonSx = {
  p: { xs: 2, sm: 3 },
};

export const evolutionCrmHintSx = {
  display: 'block',
  mt: 0.75,
  color: EVOLUTION_THEME.tealDark,
  fontWeight: 600,
};

export const evolutionCaptionMutedSx = {
  color: EVOLUTION_THEME.slate,
};

export const evolutionCaptionFaintSx = {
  color: EVOLUTION_THEME.slateFaint,
};

export const evolutionEmptyStateSx = {
  color: EVOLUTION_THEME.slateFaint,
  fontStyle: 'italic' as const,
};

export const evolutionMonospaceBlockSx = {
  mt: 0.75,
  mb: 0,
  whiteSpace: 'pre-wrap' as const,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.75rem',
  color: EVOLUTION_THEME.slateMid,
  p: 1.5,
  borderRadius: 1.5,
  bgcolor: EVOLUTION_THEME.muted,
  border: `1px solid ${EVOLUTION_THEME.borderSoft}`,
};
